<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { Plus, EditPencil, Key, Trash } from "@iconoir/vue";
import ListPage from "~/components/templates/ListPage.vue";
import ListEmpty from "~/components/templates/ListEmpty.vue";
import { useList } from "~/composables/use-list";
import { useCrud } from "~/composables/use-crud";
import { useFormat } from "~/composables/use-format";
import AdminUserFormModal from "./AdminUserFormModal.vue";
import ResetPasswordModal from "./ResetPasswordModal.vue";
import type { AdminUser } from "~~/schemas/admin-user";
import { AdminPermission } from "~~/schemas/admin-role";

defineOptions({ name: "AdminUser" });

const { t } = useI18n();
const { d } = useFormat();
const toast = useToast();
const alert = useAlert();
const authStore = useAuthStore();
const { can } = useAccess();
const { set: setTitle } = usePageTitle();

const defaultFilter = {
  keyword: "",
  status: "all",
  page: 1,
  pageSize: 50,
};

const {
  firstLoading,
  listLoading,
  load,
  itemCount,
  pageCount,
  items,
  filter,
  isEmpty,
  isSearchEmpty,
  search,
  searchDebounced,
} = useList<AdminUser, typeof defaultFilter>({
  endpoint: "/admin-users",
  defaultFilter,
});

const { del, errorMsg } = useCrud<AdminUser>({
  endpoint: "/admin-users",
});

const statusOptions = computed(() => [
  { value: "all", label: t("adminUser.statusAll") },
  { value: "deleted", label: t("adminUser.statusDeleted") },
]);

const formRef = ref<InstanceType<typeof AdminUserFormModal> | null>(null);
const resetRef = ref<InstanceType<typeof ResetPasswordModal> | null>(null);

const currentAdminId = computed(() => authStore.user?.id || null);

function isSelf(row: AdminUser) {
  return !!currentAdminId.value && row.id === currentAdminId.value;
}

function openCreate() {
  formRef.value?.open();
}

function openEdit(row: AdminUser) {
  formRef.value?.open(row.id);
}

function openResetPassword(row: AdminUser) {
  resetRef.value?.open(row.id);
}

async function confirmDelete(row: AdminUser) {
  const ok = await alert.negative({
    title: t("adminUser.deleteConfirmTitle"),
    description: t("adminUser.deleteConfirmBody", { name: row.fullName || row.email }),
    confirmLabel: t("action.delete"),
    cancelLabel: t("action.cancel"),
  });
  if (!ok) return;

  try {
    await del(row.id);
    toast.success({ title: t("message.deleteSuccess") });
    // Clamp page before reload so we only fire one fetch when the delete
    // dropped the page count below the current page.
    const nextItemCount = Math.max(0, itemCount.value - 1);
    const nextPageCount = Math.max(1, Math.ceil(nextItemCount / filter.pageSize));
    if (Number(filter.page) > nextPageCount) {
      filter.page = nextPageCount;
    }
    await load();
  } catch {
    if (errorMsg.value) toast.error({ title: errorMsg.value });
  }
}

async function onSaved() {
  await load();
}

onMounted(async () => {
  setTitle(t("adminUser.title"));
  try {
    await load();
  } catch (e: any) {
    toast.error({ title: e?.data?.message || t("adminUser.loadError") });
  }
});

watch(
  () => filter.keyword,
  () => {
    searchDebounced();
  },
);

watch(
  () => filter.status,
  () => {
    search();
  },
);

watch(
  () => filter.page,
  () => {
    load();
  },
);
</script>

<template>
  <ListPage
    :title="t('adminUser.title')"
    :first-loading="firstLoading"
    :is-empty="isEmpty"
    :is-search-empty="isSearchEmpty"
  >
    <template #status>
      <span data-testid="admin-user-list-status">
        {{
          listLoading
            ? t("ui.loading")
            : `${itemCount} ${t("adminUser.itemsSuffix")}`
        }}
      </span>
    </template>

    <template #actions>
      <Button
        v-if="can(AdminPermission.AdminUsersWrite)"
        variant="primary"
        size="small"
        data-testid="admin-user-new-btn"
        @click="openCreate"
      >
        <Plus class="icon" />
        {{ t("adminUser.add") }}
      </Button>
    </template>

    <template #empty>
      <ListEmpty
        :title="t('adminUser.emptyTitle')"
        :subtitle="t('adminUser.emptySubtitle')"
      >
        <template #action>
          <Button v-if="can(AdminPermission.AdminUsersWrite)" variant="primary" size="small" @click="openCreate">
            <Plus class="icon" />
            {{ t("adminUser.add") }}
          </Button>
        </template>
      </ListEmpty>
    </template>

    <template #search-empty>
      <ListEmpty
        :title="t('adminUser.emptySearchTitle')"
        :subtitle="t('adminUser.emptySearchSubtitle')"
      />
    </template>

    <template #filter-left>
      <FormSelect
        v-model="filter.status"
        :options="statusOptions"
        size="sm"
        class="w-full md:w-1/2"
      />
    </template>

    <template #filter-right>
      <FormSearch
        v-model="filter.keyword"
        :placeholder="t('field.search')"
        size="sm"
        class="w-full md:w-1/2"
      />
    </template>

    <template #default>
      <DataTable
        :columns="[
          { key: 'user', label: t('field.fullName'), class: 'md:min-w-[240px]' },
          { key: 'role', label: t('adminUser.columnRole'), class: 'min-w-[112px]' },
          { key: 'createTime', label: t('field.createTime'), class: 'min-w-[160px] md:table-cell' },
          { key: 'actions', align: 'right', label: t('ui.action'), class: 'min-w-[80px]' },
        ]"
        :items="items"
      >
        <template #cell-user="{ row }">
          <div class="flex items-center gap-small">
            <Avatar
              :initials="(row.fullName || row.email || '').substring(0, 2).toUpperCase()"
              size="small"
              shape="rounded"
            />
            <div class="flex flex-col min-w-0">
              <div class="flex flex-wrap items-center gap-2xsmall">
                <span class="font-semibold">{{ row.fullName || "—" }}</span>
                <Badge v-if="isSelf(row)" intent="informative" emphasis="medium" size="small">
                  {{ t("adminUser.you") }}
                </Badge>
              </div>
              <span class="text-body-small-tertiary content-neutral-medium">{{ row.email }}</span>
            </div>
          </div>
        </template>

        <template #cell-role="{ row }">
          <Badge intent="neutral" emphasis="medium" size="small">
            {{ row.role?.name || row.roleKey || "—" }}
          </Badge>
        </template>

        <template #cell-createTime="{ row }">
          {{ d(row.createTime, 'datetime') }}
        </template>

        <template #actions="{ row }">
          <div class="flex items-center justify-end gap-xsmall">
            <ButtonIcon
              v-if="can(AdminPermission.AdminUsersResetPassword)"
              variant="neutral-flat"
              size="small"
              :title="t('adminUser.resetPassword')"
              :aria-label="t('adminUser.resetPassword')"
              :disabled="row.deleteTime"
              data-testid="admin-user-row-reset"
              @click.stop="openResetPassword(row)"
            >
              <Key class="icon" />
            </ButtonIcon>

            <ButtonIcon
              v-if="can(AdminPermission.AdminUsersWrite)"
              variant="neutral-flat"
              size="small"
              :title="t('action.edit')"
              :aria-label="t('action.edit')"
              :disabled="row.deleteTime"
              data-testid="admin-user-row-edit"
              @click.stop="openEdit(row)"
            >
              <EditPencil class="icon" />
            </ButtonIcon>

            <ButtonIcon
              v-if="can(AdminPermission.AdminUsersDelete) && !row.deleteTime && !isSelf(row)"
              variant="neutral-flat"
              size="small"
              :title="t('action.delete')"
              :aria-label="t('action.delete')"
              data-testid="admin-user-row-delete"
              @click.stop="confirmDelete(row)"
            >
              <Trash class="icon" />
            </ButtonIcon>
          </div>
        </template>
      </DataTable>

      <Pagination
        v-if="pageCount > 1"
        v-model:page="filter.page"
        :count="itemCount"
        :page-size="filter.pageSize"
        class="mt-medium"
      />
    </template>
  </ListPage>

  <AdminUserFormModal ref="formRef" @saved="onSaved" />

  <ResetPasswordModal ref="resetRef" />
</template>
